import * as Yup from 'yup';
import { FormatMessageFunction } from '@spa/lib/i18n/common/FormatMessageFunction';
import { ProductReviewFormField } from '@spa/generated/graphql/graphql';
import { Schema } from 'yup';
import { WriteReviewFormFields } from './bazaarvoiceWriteReviewModalTypes';
import { createEmailSchema } from '@spa/lib/formsSchemas/commonValidations';

const REVIEW_TEXT_LENGTH = 5000;
const REVIEW_TITLE_LENGTH = 100;
const NICKNAME_LENGTH = 50;

// Bazaarvoice's custom, account-configured questions all use this id prefix, distinct from the
// standard/reserved fields (rating, reviewtext, hostedauthentication_*, ...).
const EXTRA_FIELD_ID_PREFIX = 'contextdatavalue_';

const findField = (
  fields: ReadonlyArray<ProductReviewFormField>,
  id: string
): ProductReviewFormField | undefined =>
  fields.find((field) => field.id.toLowerCase() === id.toLowerCase());

const buildExtraFieldSchema = (
  field: ProductReviewFormField,
  formatMessage: FormatMessageFunction,
  formsFormatMessage: FormatMessageFunction
): Yup.AnySchema => {
  if (field.type === 'BooleanInput') {
    return field.required
      ? Yup.boolean().oneOf([true], formsFormatMessage('errors.required'))
      : Yup.boolean();
  }

  let fieldSchema: Yup.StringSchema = Yup.string();

  if (field.required) {
    fieldSchema = fieldSchema.required(formsFormatMessage('errors.required'));
  }
  if (field.minLength) {
    fieldSchema = fieldSchema.min(
      field.minLength,
      formatMessage('errors.tooShort', { min: field.minLength })
    );
  }
  if (field.maxLength) {
    fieldSchema = fieldSchema.max(
      field.maxLength,
      formsFormatMessage('errors.defaultMax', { max: field.maxLength })
    );
  }

  return fieldSchema;
};

// Bazaarvoice enforces its own min/max length per field (fetched via getProductReviewForm),
// which can differ from our defaults -- a review rejected purely for length otherwise surfaces
// as an opaque, unmappable server error with no actionable message. Applying the account's real
// constraints client-side catches it before submission instead.
const applyFetchedLengthConstraints = (
  schema: Yup.StringSchema,
  field: ProductReviewFormField | undefined,
  fallbackMaxLength: number,
  formatMessage: FormatMessageFunction,
  formsFormatMessage: FormatMessageFunction
): Yup.StringSchema => {
  let result: Yup.StringSchema = schema;
  const maxLength: number = field?.maxLength ?? fallbackMaxLength;

  if (field?.minLength) {
    result = result.min(
      field.minLength,
      formatMessage('errors.tooShort', { min: field.minLength })
    );
  }

  return result.max(maxLength, formsFormatMessage('errors.defaultMax', { max: maxLength }));
};

const bazaarvoiceWriteReviewModalSchema = (
  formatMessage: FormatMessageFunction,
  formsFormatMessage: FormatMessageFunction,
  reviewFormFields: ReadonlyArray<ProductReviewFormField> = []
): Schema<WriteReviewFormFields> => {
  const extraFields: ReadonlyArray<ProductReviewFormField> = reviewFormFields.filter((field) =>
    field.id.toLowerCase().startsWith(EXTRA_FIELD_ID_PREFIX)
  );
  const reviewTextField = findField(reviewFormFields, 'reviewtext');
  const titleField = findField(reviewFormFields, 'title');
  const nicknameField = findField(reviewFormFields, 'usernickname');

  return Yup.object({
    rating: Yup.number()
      .min(1, formatMessage('errors.ratingRequired'))
      .required(formatMessage('errors.ratingRequired')),
    reviewText: applyFetchedLengthConstraints(
      Yup.string().trim(),
      reviewTextField,
      REVIEW_TEXT_LENGTH,
      formatMessage,
      formsFormatMessage
    ).required(formsFormatMessage('errors.required')),
    reviewTitle: applyFetchedLengthConstraints(
      Yup.string().trim(),
      titleField,
      REVIEW_TITLE_LENGTH,
      formatMessage,
      formsFormatMessage
    ).required(formsFormatMessage('errors.required')),
    nickname: applyFetchedLengthConstraints(
      Yup.string().trim(),
      nicknameField,
      NICKNAME_LENGTH,
      formatMessage,
      formsFormatMessage
    ).required(formsFormatMessage('errors.required')),
    email: createEmailSchema(formsFormatMessage),
    recommendation: Yup.mixed<'yes' | 'no' | ''>()
      .oneOf(['yes', 'no'], formatMessage('errors.recommendationRequired'))
      .required(formatMessage('errors.recommendationRequired')),
    photos: Yup.array().of(Yup.mixed<File>().required()).default([]).nonNullable(),
    termsAccepted: Yup.boolean()
      .oneOf([true], formatMessage('errors.termsRequired'))
      .required(formatMessage('errors.termsRequired')),
    extraFields: Yup.object(
      Object.fromEntries(
        extraFields.map((field) => [
          field.id,
          buildExtraFieldSchema(field, formatMessage, formsFormatMessage)
        ])
      )
    ).default({})
  });
};

export default bazaarvoiceWriteReviewModalSchema;
